import { useCallback, useEffect, useRef, useState } from 'react';

export type PropertySectionKey =
    | 'basicInfo'
    | 'propertyFacts'
    | 'tourSettings'
    | 'applicationLinks';

const SCROLL_SPY_OFFSET = 80;
const SCROLL_SPY_SUPPRESS_MS = 800;

export function usePropertySectionNavigation(visibleSections: PropertySectionKey[]) {
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Partial<Record<PropertySectionKey, HTMLElement | null>>>({});
    const spySuppressTimeoutRef = useRef<number | null>(null);
    const [activeSection, setActiveSection] = useState<PropertySectionKey>('basicInfo');

    const setSectionRef = useCallback(
        (key: PropertySectionKey) => (element: HTMLElement | null) => {
            sectionRefs.current[key] = element;
        },
        []
    );

    useEffect(() => () => {
        if (spySuppressTimeoutRef.current != null) {
            window.clearTimeout(spySuppressTimeoutRef.current);
        }
    }, []);

    const scrollToSection = useCallback((key: PropertySectionKey) => {
        setActiveSection(key);

        if (spySuppressTimeoutRef.current != null) {
            window.clearTimeout(spySuppressTimeoutRef.current);
        }
        spySuppressTimeoutRef.current = window.setTimeout(() => {
            spySuppressTimeoutRef.current = null;
        }, SCROLL_SPY_SUPPRESS_MS);

        const container = contentRef.current;
        const section = sectionRefs.current[key];
        if (container && section) {
            const titleElement = section.querySelector<HTMLElement>('[data-property-section-title]') ?? section;
            const containerTop = container.getBoundingClientRect().top;
            const titleTop = titleElement.getBoundingClientRect().top;
            container.scrollTo({
                top: container.scrollTop + titleTop - containerTop - 8,
                behavior: 'smooth',
            });
        }
    }, []);

    const handleContentScroll = useCallback(() => {
        const container = contentRef.current;
        if (!container || spySuppressTimeoutRef.current != null) {
            return;
        }

        let current = visibleSections[0];
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
        if (isAtBottom) {
            current = visibleSections[visibleSections.length - 1];
        } else {
            const containerTop = container.getBoundingClientRect().top;
            for (const key of visibleSections) {
                const element = sectionRefs.current[key];
                if (element && element.getBoundingClientRect().top - containerTop <= SCROLL_SPY_OFFSET) {
                    current = key;
                }
            }
        }

        setActiveSection(current);
    }, [visibleSections]);

    return {
        activeSection,
        contentRef,
        handleContentScroll,
        scrollToSection,
        setSectionRef,
    };
}
